package com.elan.edubridge.ui.member

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.AccountDeletionBlocker
import com.elan.edubridge.shared.domain.model.AccountDeletionBlockerReason
import com.elan.edubridge.shared.domain.model.AccountDeletionRetention
import com.elan.edubridge.shared.domain.model.AccountDeletionState
import com.elan.edubridge.shared.domain.model.AccountDeletionStatus
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.staff.Toast
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLInputElement

/** `/app/me/account-deletion` — 로그인한 사용자가 자기 계정 삭제를 요청·취소하는 화면. */
object AccountDeletionPage {
    private val scope = MainScope()
    private val accountRepository = SharedGraph.createAccountRepository()
    private var confirmationRequested = false

    fun render(session: SessionState) {
        confirmationRequested = false
        scope.launch {
            val context = loadMemberContext(session)
            MemberShell.render(context, activeTab = MemberShell.Tab.SETTINGS, bodyHtml = loadingHtml())
            paint(context, accountRepository.accountDeletionStatus())
        }
    }

    private suspend fun paint(context: MemberContext, state: AccountDeletionState) {
        MemberShell.render(context, activeTab = MemberShell.Tab.SETTINGS, bodyHtml = body(state))
        showError(state.errorMessage)
        wire(context, state)
    }

    private fun loadingHtml(): String = """
        <h1 class="eb-page-title">계정 삭제</h1>
        <section class="eb-card" aria-live="polite">${icon("icon-refresh", "md", "eb-spin")}삭제 가능 여부를 확인하고 있어요.</section>
    """.trimIndent()

    private fun body(state: AccountDeletionState): String {
        val stateContent = when {
            state.failure != null -> unavailableHtml() + if (state.isBlocked) blockedHtml(state.blockers) else ""
            state.isBlocked -> blockedHtml(state.blockers)
            state.isPending -> pendingHtml(state)
            state.status == AccountDeletionStatus.PURGING -> purgingHtml()
            state.status == AccountDeletionStatus.COMPLETED -> completedHtml()
            else -> requestHtml(state)
        }
        val deletionInformation = if (state.failure == null) {
            """
                <p>삭제 요청 후 서버가 계정 상태를 다시 확인하고 ${state.graceDays}일의 유예 기간이 끝난 뒤 다음 파기 작업에서 계정을 삭제해요. 유예 기간에는 계속 로그인할 수 있고 요청을 취소할 수 있어요.</p>
                <p class="eb-meta">학원의 수강생 대장과 출결 이력은 학원 기록으로 남고, 계정과의 연결만 해제될 수 있어요.</p>
                ${retentionHtml(state.retentionNotice)}
            """.trimIndent()
        } else {
            ""
        }
        return """
            <h1 class="eb-page-title">계정 삭제</h1>
            <section class="eb-card">
                $deletionInformation
                <p class="eb-form-error" id="account-deletion-error" role="alert" hidden></p>
                $stateContent
            </section>
        """.trimIndent()
    }

    private fun requestHtml(state: AccountDeletionState): String {
        if (!confirmationRequested) {
            return "<button class=\"eb-button eb-button--ghost\" id=\"request-deletion-button\" type=\"button\">삭제 요청</button>"
        }
        return """
            <div class="eb-field eb-field--checkbox">
                <input id="confirm-deletion" type="checkbox">
                <label for="confirm-deletion">${state.graceDays}일 유예 기간 뒤 계정이 파기되며, 남는 학원 기록이 있다는 점을 이해했습니다.</label>
            </div>
            <button class="eb-button eb-button--primary" id="submit-deletion-button" type="button">삭제 요청 제출</button>
            <button class="eb-button eb-button--link" id="cancel-confirmation-button" type="button">취소</button>
        """.trimIndent()
    }

    private fun pendingHtml(state: AccountDeletionState): String = """
        <div class="eb-empty-state" aria-live="polite">
            ${icon("icon-check", "xl")}
            <h2>삭제 요청이 접수되었어요</h2>
            <p>유예 기간이 끝난 뒤 다음 파기 작업에서 계정을 삭제해요. 유예 종료 시각: ${state.scheduledPurgeAt?.escapeHtml() ?: "서버 확인 중"}</p>
            <button class="eb-button eb-button--ghost" id="cancel-deletion-button" type="button">삭제 요청 취소</button>
        </div>
    """.trimIndent()

    private fun blockedHtml(blockers: List<AccountDeletionBlocker>): String = """
        <div class="eb-empty-state">
            <h2>먼저 학원 관리를 정리해 주세요</h2>
            <p>학원장 또는 마지막 관리자인 계정은 학원을 관리할 사람이 없어지므로 지금 삭제할 수 없어요.</p>
            <ul class="eb-list">
                ${blockers.joinToString(separator = "") { blocker -> "<li>${blocker.academyName.escapeHtml()} · ${blockerReasonLabel(blocker.reason)}</li>" }}
            </ul>
        </div>
    """.trimIndent()

    private fun purgingHtml(): String = """
        <div class="eb-empty-state" aria-live="polite">
            ${icon("icon-refresh", "xl", "eb-spin")}
            <h2>계정 삭제를 처리하고 있어요</h2>
            <p>파기가 시작되어 이제 요청을 취소할 수 없어요.</p>
        </div>
    """.trimIndent()

    private fun completedHtml(): String = """
        <div class="eb-empty-state">
            <h2>계정 삭제가 완료되었어요</h2>
            <p>이 계정은 더 이상 사용할 수 없어요.</p>
        </div>
    """.trimIndent()

    private fun unavailableHtml(): String = """
        <p class="eb-meta">삭제 상태를 확인하지 못했어요. 잠시 후 다시 시도해 주세요.</p>
        <button class="eb-button eb-button--ghost" id="reload-deletion-status-button" type="button">다시 확인</button>
    """.trimIndent()

    private fun retentionHtml(retentionNotice: List<AccountDeletionRetention>): String {
        if (retentionNotice.isEmpty()) return ""
        return """
            <div class="eb-card eb-card--muted">
                <h2>삭제 후에도 남는 기록</h2>
                <ul class="eb-list">
                    ${retentionNotice.joinToString(separator = "") { retention -> "<li>${retentionLabel(retention)}</li>" }}
                </ul>
            </div>
        """.trimIndent()
    }

    private fun wire(context: MemberContext, state: AccountDeletionState) {
        (document.getElementById("request-deletion-button") as? HTMLButtonElement)?.onclick = {
            confirmationRequested = true
            scope.launch { paint(context, state) }
            null
        }
        (document.getElementById("cancel-confirmation-button") as? HTMLButtonElement)?.onclick = {
            confirmationRequested = false
            scope.launch { paint(context, state) }
            null
        }
        (document.getElementById("submit-deletion-button") as? HTMLButtonElement)?.onclick = { button ->
            val checkbox = document.getElementById("confirm-deletion") as? HTMLInputElement
            if (checkbox?.checked == true) {
                val submitButton = button.currentTarget as? HTMLButtonElement
                submitButton?.disabled = true
                scope.launch {
                    confirmationRequested = false
                    paint(context, accountRepository.requestAccountDeletion(reason = null))
                }
            } else {
                showError("삭제 요청 내용을 확인해 주세요.")
            }
            null
        }
        (document.getElementById("cancel-deletion-button") as? HTMLButtonElement)?.onclick = { button ->
            val cancelButton = button.currentTarget as? HTMLButtonElement
            cancelButton?.disabled = true
            scope.launch {
                val result = accountRepository.cancelAccountDeletion()
                paint(context, result)
                if (result.failure == null && result.status == AccountDeletionStatus.CANCELLED) {
                    Toast.success("삭제 요청을 취소했어요.")
                }
            }
            null
        }
        (document.getElementById("reload-deletion-status-button") as? HTMLButtonElement)?.onclick = {
            scope.launch { paint(context, accountRepository.accountDeletionStatus()) }
            null
        }
    }

    private fun showError(message: String?) {
        if (message.isNullOrBlank()) return
        val error = document.getElementById("account-deletion-error") ?: return
        error.textContent = message
        error.removeAttribute("hidden")
    }

    private fun blockerReasonLabel(reason: AccountDeletionBlockerReason): String = when (reason) {
        AccountDeletionBlockerReason.ACADEMY_OWNER -> "학원장"
        AccountDeletionBlockerReason.LAST_ADMIN -> "마지막 관리자"
    }

    private fun retentionLabel(retention: AccountDeletionRetention): String = when (retention) {
        AccountDeletionRetention.ACADEMY_RECORDS_UNLINKED -> "학원의 수강생 대장과 출결 이력은 남고, 계정 연결만 해제됩니다."
        AccountDeletionRetention.BOARD_CONTENT_ANONYMISED -> "게시글과 댓글 본문은 남고 작성자 표시는 지워집니다."
        AccountDeletionRetention.AUDIT_LOG_RETAINED -> "서비스 운영과 보안에 필요한 감사 기록은 보존됩니다."
    }
}
